(() => {
  'use strict';

  const RELEASE = 'noryli-20260827.1';
  if (window.__NORYLI_APP_WORKFLOW_RELEASE__ === RELEASE) return;
  window.__NORYLI_APP_WORKFLOW_RELEASE__ = RELEASE;

  const nativeFetch = window.fetch.bind(window);
  const product = window.yx_product && typeof window.yx_product === 'object'
    ? window.yx_product
    : {};
  const cache = new Map();
  const CACHE_TTL = 120000;
  const RESTORE_TTL = 15 * 60 * 1000;
  const restoreKey = `noryli-purchase:${location.pathname}`;
  let checkoutRunning = false;
  let activeButton = null;

  const state = {
    variantId: '',
    quantity: 1,
    unitCount: 1,
    sellingPlanId: '',
    finalPrice: 0,
    averagePrice: 0,
    selectionSignature: '',
    selectionVersion: 0,
    checkoutPhase: 'idle',
  };

  const gid = (type, id) => `gid://shopify/${type}/${String(id).replace(/^gid:\/\/shopify\/[^/]+\//, '')}`;
  const numericId = (value) => String(value || '').split('/').pop();
  const currency = () => window.Shopify?.currency?.active || 'USD';
  const money = (cents) => new Intl.NumberFormat(document.documentElement.lang || 'en-US', {
    style: 'currency',
    currency: currency(),
  }).format(Number(cents || 0) / 100);
  const amount = (value) => Number(value || 0);
  const toCents = (value) => Math.round(amount(value) * 100);

  const selectedVariantInput = () =>
    document.querySelector('input[data-variant-selector]:checked') ||
    document.querySelector('input[data-variant-selector]');

  const selectedPlanInput = () =>
    document.querySelector('input[name="subscription_enabled"][data-selling-plan-id]:checked');

  const selectedVariant = (variantId) => (product.variants || []).find(
    (variant) => String(variant.id) === String(variantId)
  );

  const planAllocation = (variant, sellingPlanId) => {
    if (!variant || !sellingPlanId) return null;
    return (variant.selling_plan_allocations || []).find(
      (allocation) => String(allocation.selling_plan_id) === String(sellingPlanId)
    ) || null;
  };

  const getSnapshot = () => {
    const input = selectedVariantInput();
    const fallbackId = document.querySelector('[data-variant-id-input]')?.value || '';
    const variantId = String(input?.value || fallbackId);
    const variant = selectedVariant(variantId);
    if (!variant) throw new Error('The selected bundle could not be read.');

    const quantityInput = document.querySelector('[data-quantity] input[name="quantity"]');
    const quantity = Math.max(1, Math.floor(Number(quantityInput?.value || 1)) || 1);
    const unitCount = Math.max(1, Number(input?.dataset?.unitCount || 1) || 1);
    const plan = selectedPlanInput();
    const sellingPlanId = String(plan?.dataset?.sellingPlanId || '');
    const allocation = planAllocation(variant, sellingPlanId);
    const finalPrice = Number(allocation?.price ?? variant.price ?? 0);
    const signature = [variantId, quantity, unitCount, sellingPlanId || 'one-time', finalPrice].join(':');

    return {
      variant,
      variantId,
      quantity,
      unitCount,
      plan,
      allocation,
      sellingPlanId,
      finalPrice,
      averagePrice: Math.round(finalPrice / unitCount),
      selectionSignature: signature,
    };
  };

  const render = (snapshot) => {
    document.querySelectorAll('input[data-variant-selector]').forEach((input) => {
      input.closest('.reference-bundle')?.classList.toggle('is-selected', input.checked);
    });

    const hiddenVariant = document.querySelector('[data-variant-id-input]');
    if (hiddenVariant) hiddenVariant.value = snapshot.variantId;

    document.querySelectorAll('.yx-main-product-page [data-variant-id]').forEach((button) => {
      button.dataset.variantId = snapshot.variantId;
    });

    document.querySelectorAll('[data-price], [data-sticky-price]').forEach((element) => {
      element.textContent = money(snapshot.finalPrice);
    });

    const compare = Number(snapshot.variant.compare_at_price || 0);
    document.querySelectorAll('[data-compare-price]').forEach((element) => {
      element.hidden = compare <= snapshot.finalPrice;
      element.textContent = money(compare);
    });

    document.querySelectorAll('input[data-variant-selector]').forEach((input) => {
      const variant = selectedVariant(input.value);
      const allocation = planAllocation(variant, snapshot.sellingPlanId);
      const finalPrice = Number(allocation?.price ?? variant?.price ?? 0);
      const units = Math.max(1, Number(input.dataset.unitCount || 1) || 1);
      const card = input.closest('.reference-bundle');
      const averageNode = card?.querySelector('[data-bundle-average]');
      const totalNode = card?.querySelector('[data-bundle-total]');
      if (averageNode) averageNode.innerHTML = `${money(Math.round(finalPrice / units))} <small>/ piece</small>`;
      if (totalNode) totalNode.textContent = `Total: ${money(finalPrice)}`;
    });
  };

  const syncState = ({ prewarm = true } = {}) => {
    const snapshot = getSnapshot();
    const changed = snapshot.selectionSignature !== state.selectionSignature;
    if (changed) state.selectionVersion += 1;
    Object.assign(state, snapshot);
    if (state.checkoutPhase === 'error') state.checkoutPhase = 'idle';
    render(snapshot);
    clearError();
    if (prewarm && (changed || !cache.has(snapshot.selectionSignature))) schedulePrewarm(snapshot, state.selectionVersion);
    return snapshot;
  };

  const cartQuery = `mutation NoryliCreateIndependentCart($merchandiseId: ID!, $quantity: Int!, $sellingPlanId: ID) {
    cartCreate(input: { lines: [{ merchandiseId: $merchandiseId, quantity: $quantity, sellingPlanId: $sellingPlanId }] }) {
      cart {
        id
        totalQuantity
        cost {
          subtotalAmount { amount currencyCode }
          totalAmount { amount currencyCode }
        }
        lines(first: 5) {
          nodes {
            quantity
            cost {
              amountPerQuantity { amount currencyCode }
              totalAmount { amount currencyCode }
            }
            merchandise {
              ... on ProductVariant {
                id
                title
                sku
                image { url }
                product { id title handle }
              }
            }
            sellingPlanAllocation { sellingPlan { id name } }
          }
        }
      }
      userErrors { field message }
    }
  }`;

  const createIndependentCart = async (snapshot) => {
    const response = await nativeFetch('/api/2026-07/graphql.json', {
      method: 'POST',
      headers: { 'Content-Type': 'application/json' },
      body: JSON.stringify({
        query: cartQuery,
        variables: {
          merchandiseId: gid('ProductVariant', snapshot.variantId),
          quantity: snapshot.quantity,
          sellingPlanId: snapshot.sellingPlanId ? gid('SellingPlan', snapshot.sellingPlanId) : null,
        },
      }),
    });
    const payload = await response.json().catch(() => ({}));
    const result = payload?.data?.cartCreate;
    const errors = [...(payload?.errors || []), ...(result?.userErrors || [])];
    const cart = result?.cart;
    const lines = cart?.lines?.nodes || [];
    const line = lines[0];
    const actualPlanId = numericId(line?.sellingPlanAllocation?.sellingPlan?.id);
    const expectedPlanId = snapshot.sellingPlanId || '';

    if (
      !response.ok || errors.length || !cart?.id || lines.length !== 1 ||
      String(numericId(line?.merchandise?.id)) !== String(snapshot.variantId) ||
      Number(line?.quantity) !== snapshot.quantity ||
      Number(cart?.totalQuantity) !== snapshot.quantity ||
      String(actualPlanId) !== String(expectedPlanId)
    ) {
      const message = errors.map((entry) => entry.message).filter(Boolean).join(', ');
      throw new Error(message || 'The checkout cart did not match the selected purchase.');
    }

    return {
      cart,
      line,
      cartToken: cart.id,
      selectionSignature: snapshot.selectionSignature,
      createdAt: Date.now(),
    };
  };

  const prepare = (snapshot = getSnapshot()) => {
    const signature = snapshot.selectionSignature;
    const existing = cache.get(signature);
    if (existing && Date.now() - existing.createdAt <= CACHE_TTL) {
      return existing.promise || Promise.resolve(existing.value);
    }

    const record = { createdAt: Date.now(), promise: null, value: null };
    record.promise = createIndependentCart(snapshot).then((prepared) => {
      record.value = prepared;
      record.promise = null;
      record.createdAt = Date.now();
      return prepared;
    }).catch((error) => {
      cache.delete(signature);
      throw error;
    });
    cache.set(signature, record);
    return record.promise;
  };

  const schedulePrewarm = (snapshot, version) => {
    const run = () => {
      if (version !== state.selectionVersion || snapshot.selectionSignature !== state.selectionSignature) return;
      state.checkoutPhase = 'prewarming';
      prepare(snapshot).then(() => {
        if (version === state.selectionVersion && state.checkoutPhase === 'prewarming') state.checkoutPhase = 'ready';
      }).catch((error) => {
        if (version === state.selectionVersion && state.checkoutPhase === 'prewarming') state.checkoutPhase = 'idle';
        console.warn('[NORYLI checkout prewarm]', error.message);
      });
    };
    window.setTimeout(run, 0);
    window.setTimeout(run, 350);
  };

  const getShopId = () => {
    const id = String(window.Shopify?.shop || '').split('.')[0];
    if (!id) throw new Error('The store identity could not be read.');
    return id;
  };

  const buildCheckoutPayload = (snapshot, prepared) => {
    const { cart, line } = prepared;
    const unitPrice = amount(line.cost.amountPerQuantity.amount);
    const linePrice = amount(line.cost.totalAmount.amount);
    const totalPrice = amount(cart.cost.totalAmount.amount);
    const subtotalPrice = amount(cart.cost.subtotalAmount.amount);
    const merchandise = line.merchandise;
    const plan = line.sellingPlanAllocation?.sellingPlan;
    const originalUnitPrice = Number(snapshot.variant.price || 0) / 100;
    const totalDiscount = Math.max(0, originalUnitPrice * snapshot.quantity - linePrice);

    return {
      cart: {
        items: [{
          variantId: merchandise.id,
          quantity: snapshot.quantity,
          title: merchandise.title && merchandise.title !== 'Default Title'
            ? `${merchandise.product.title} - ${merchandise.title}`
            : merchandise.product.title,
          variant: merchandise.title || '',
          originalPrice: originalUnitPrice,
          price: unitPrice,
          linePrice,
          totalDiscount,
          productId: merchandise.product.id,
          url: `/products/${merchandise.product.handle}?variant=${snapshot.variantId}`,
          image: merchandise.image?.url || snapshot.variant.featured_image?.src || product.featured_image || '',
          weight: Number(snapshot.variant.grams || snapshot.variant.weight || 0),
          weightUnit: 'GRAMS',
          currencyCode: line.cost.totalAmount.currencyCode || currency(),
          sellingPlanId: plan ? Number(numericId(plan.id)) : null,
          sellingPlanName: plan?.name || '',
          sellingPlanGroupId: snapshot.allocation?.selling_plan_group_id || '',
        }],
        cartToken: prepared.cartToken,
        originalTotalPrice: originalUnitPrice * snapshot.quantity,
        subtotalPrice,
        totalPrice,
        totalDiscount,
        totalWeight: Number(snapshot.variant.grams || snapshot.variant.weight || 0) * snapshot.quantity,
        itemCount: snapshot.quantity,
        currency: cart.cost.totalAmount.currencyCode || currency(),
      },
      position: {
        countryCode: window.Shopify?.country || '',
        regionCode: '',
        city: '',
      },
      checkoutId: '',
      language: window.Shopify?.locale || 'en',
      extra: {
        view: 'default',
        sourceUrl: `${location.origin}${location.pathname}?view=default&appWorkflow=${RELEASE}`,
        appWorkflow: RELEASE,
        selectionSignature: snapshot.selectionSignature,
        cookie_id: '',
      },
    };
  };

  const checkoutApi = async (apiPath, body) => {
    const response = await nativeFetch(`https://us.ouyifyapi.com${apiPath}`, {
      method: 'POST',
      headers: {
        'Content-Type': 'application/json',
        'Shopify-Id': getShopId(),
        'oyAppVersion': 'v2',
      },
      body: JSON.stringify(body),
    });
    const payload = await response.json().catch(() => ({}));
    if (!response.ok || (payload.code != null && Number(payload.code) !== 200)) {
      throw new Error(payload.message || `Checkout failed (${response.status}).`);
    }
    return payload.data ?? payload;
  };

  const resolvePayPalUrl = (payload) => {
    const data = payload?.data ?? payload ?? {};
    const direct = String(data.paypalPaymentUrl || '').trim();
    if (direct && !/\/thanks|\/paypal\/return|\/confirm-address/i.test(direct)) return direct;
    const links = data.paypalOrder?.links;
    if (Array.isArray(links)) {
      for (const rel of ['payer-action', 'approve']) {
        const url = String(links.find((link) => link?.rel === rel)?.href || '').trim();
        if (/paypal\.com/i.test(url)) return url;
      }
    }
    return String(data.redirectUrl || data.paymentUrl || '').trim();
  };

  const addPayPalFunding = (url) => {
    const target = new URL(url, location.origin);
    if (!target.searchParams.has('useraction')) target.searchParams.set('useraction', 'commit');
    if (!target.searchParams.has('fundingSource')) target.searchParams.set('fundingSource', 'paypal');
    return target.href;
  };

  const showProcessing = (button) => {
    activeButton = button;
    if (button) {
      button.dataset.noryliOriginalHtml = button.innerHTML;
      button.disabled = true;
      button.setAttribute('aria-busy', 'true');
      button.textContent = button.id === 'yx_paypal_checkout' ? 'CONNECTING TO PAYPAL…' : 'LOADING SECURE CHECKOUT…';
    }
    document.querySelector('#noryli-processing-overlay')?.remove();
    const overlay = document.createElement('div');
    overlay.id = 'noryli-processing-overlay';
    overlay.className = 'noryli-processing-overlay';
    overlay.setAttribute('role', 'status');
    overlay.setAttribute('aria-live', 'assertive');
    overlay.innerHTML = '<div class="noryli-processing-card"><i class="noryli-processing-ring" aria-hidden="true"></i><strong>Processing secure checkout</strong><span>Please keep this page open.</span></div>';
    document.documentElement.classList.add('noryli-checkout-processing');
    document.body.append(overlay);
  };

  const hideProcessing = () => {
    document.querySelector('#noryli-processing-overlay')?.remove();
    document.documentElement.classList.remove('noryli-checkout-processing');
    if (activeButton) {
      if (activeButton.dataset.noryliOriginalHtml) activeButton.innerHTML = activeButton.dataset.noryliOriginalHtml;
      delete activeButton.dataset.noryliOriginalHtml;
      activeButton.disabled = false;
      activeButton.removeAttribute('aria-busy');
    }
    activeButton = null;
    checkoutRunning = false;
    if (state.checkoutPhase === 'navigating') state.checkoutPhase = 'ready';
  };

  const clearError = () => document.querySelector('#noryli-checkout-error')?.remove();
  const showError = (message) => {
    clearError();
    const panel = document.createElement('div');
    panel.id = 'noryli-checkout-error';
    panel.className = 'noryli-checkout-error';
    panel.setAttribute('role', 'alert');
    panel.textContent = message || 'Checkout could not be started. Please try again.';
    document.querySelector('.xy-custom-button-pannel')?.prepend(panel);
  };

  const saveRestoreState = (snapshot) => {
    try {
      sessionStorage.setItem(restoreKey, JSON.stringify({
        at: Date.now(),
        variantId: snapshot.variantId,
        sellingPlanId: snapshot.sellingPlanId,
        quantity: snapshot.quantity,
      }));
    } catch (_) {}
  };

  const restoreSelection = () => {
    try {
      const raw = sessionStorage.getItem(restoreKey);
      sessionStorage.removeItem(restoreKey);
      if (!raw) return;
      const restored = JSON.parse(raw);
      if (!restored.at || Date.now() - restored.at > RESTORE_TTL) return;

      const variantInput = [...document.querySelectorAll('input[data-variant-selector]')].find(
        (input) => String(input.value) === String(restored.variantId)
      );
      if (variantInput && !variantInput.disabled) variantInput.checked = true;

      document.querySelectorAll('input[name="subscription_enabled"][data-selling-plan-id]').forEach((input) => {
        input.checked = String(input.dataset.sellingPlanId) === String(restored.sellingPlanId || '');
      });

      const quantityInput = document.querySelector('[data-quantity] input[name="quantity"]');
      if (quantityInput) quantityInput.value = Math.max(1, Number(restored.quantity || 1));
    } catch (_) {
      sessionStorage.removeItem(restoreKey);
    }
  };

  const startCheckout = async (button) => {
    if (checkoutRunning) return;
    checkoutRunning = true;
    let navigationStarted = false;
    let snapshot;
    try {
      snapshot = syncState({ prewarm: false });
      const version = state.selectionVersion;
      state.checkoutPhase = 'preparing';
      showProcessing(button);
      const prepared = await prepare(snapshot);
      if (version !== state.selectionVersion || prepared.selectionSignature !== state.selectionSignature) {
        throw new Error('Your selection changed. Please review it and try again.');
      }
      const payload = buildCheckoutPayload(snapshot, prepared);
      saveRestoreState(snapshot);
      state.checkoutPhase = 'creating-checkout';

      if (button?.id === 'yx_paypal_checkout') {
        const created = await checkoutApi('/api/checkout/paypal', payload);
        const paymentUrl = resolvePayPalUrl(created);
        if (!paymentUrl) throw new Error('PayPal did not return a payment page.');
        state.checkoutPhase = 'navigating';
        navigationStarted = true;
        window.location.assign(addPayPalFunding(paymentUrl));
        return;
      }

      const checkout = await checkoutApi('/api/checkout', payload);
      if (!checkout.checkoutId) throw new Error('The secure checkout page was not returned.');
      state.checkoutPhase = 'navigating';
      navigationStarted = true;
      window.location.assign(`/a/s/checkout/${checkout.checkoutId}`);
    } catch (error) {
      console.error('[NORYLI app checkout]', error);
      state.checkoutPhase = 'error';
      showError(error.message || 'Checkout could not be started. Please try again.');
    } finally {
      if (!navigationStarted) hideProcessing();
    }
  };

  const bind = () => {
    restoreSelection();
    syncState();
    document.querySelectorAll('input[data-variant-selector], input[name="subscription_enabled"]').forEach((input) => {
      input.addEventListener('change', () => syncState());
    });
    const quantity = document.querySelector('[data-quantity] input[name="quantity"]');
    quantity?.addEventListener('input', () => syncState());
    quantity?.addEventListener('change', () => syncState());
    document.querySelector('[data-quantity]')?.addEventListener('click', (event) => {
      if (event.target.closest('button')) window.setTimeout(() => syncState(), 0);
    });
  };

  window.addEventListener('pagehide', hideProcessing);
  window.addEventListener('pageshow', hideProcessing);
  if (document.readyState === 'loading') document.addEventListener('DOMContentLoaded', bind, { once: true });
  else bind();

  window.__NORYLI_APP_WORKFLOW__ = {
    release: RELEASE,
    getState: () => ({ ...state }),
    prepare: () => prepare(syncState({ prewarm: false })),
    buildCheckoutPayload: async () => {
      const snapshot = syncState({ prewarm: false });
      return buildCheckoutPayload(snapshot, await prepare(snapshot));
    },
  };
  document.documentElement.dataset.noryliAppWorkflow = RELEASE;
  window.dispatchEvent(new CustomEvent('noryli:app-workflow-ready', { detail: startCheckout }));
})();
